import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * CourseSettingsTab - Einstellungen Tab in der CourseView
 * 
 * Zeigt Kurs-Einstellungen:
 * - Name, Beschreibung bearbeiten
 * - Icon & Farbe ändern
 * - Gefahrenzone (Archivieren, Löschen)
 */

// Common icons for courses
const COURSE_ICONS = ['📚', '📗', '📘', '📕', '📙', '💻', '🎨', '🔬', '📐', '🌍', '🎵', '⚽'];

// Common colors for courses
const COURSE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

export default function CourseSettingsTab({
  course,
  onUpdate,
  onDelete,
  isSubmitting
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '📚',
    color: '#3B82F6'
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with course data
  useEffect(() => {
    if (course) {
      setFormData({
        name: course.name || '',
        description: course.description || '',
        icon: course.icon || '📚',
        color: course.color || '#3B82F6'
      });
      setHasChanges(false);
    }
  }, [course]);

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Handle save
  const handleSave = () => {
    onUpdate(formData);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Course Edit Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Kurs bearbeiten
        </h3>
        
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder="Kursname..."
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Beschreibung
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white resize-none"
              placeholder="Kursbeschreibung..."
            />
          </div>

          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Icon
            </label>
            <div className="flex flex-wrap gap-2">
              {COURSE_ICONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => handleChange('icon', icon)}
                  className={`w-10 h-10 text-xl rounded-lg transition-all ${
                    formData.icon === icon
                      ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500'
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Farbe
            </label>
            <div className="flex flex-wrap gap-2">
              {COURSE_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleChange('color', color)}
                  className={`w-10 h-10 rounded-lg transition-all ${
                    formData.color === color
                      ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-gray-800'
                      : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg theme-transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Speichern...' : 'Änderungen speichern'}
            </button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-danger-200 dark:border-danger-800 p-6">
        <h3 className="text-lg font-semibold text-danger-600 dark:text-danger-400 mb-4">
          Gefahrenzone
        </h3>
        
        <div className="space-y-4">
          {/* Archive */}
          <div className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">
                ⚠️ Kurs archivieren
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Der Kurs wird ausgeblendet, kann aber wiederhergestellt werden.
              </p>
            </div>
            <button
              onClick={() => alert('Archivieren wird noch implementiert')}
              className="px-4 py-2 text-sm font-medium text-warning-700 dark:text-warning-300 bg-warning-100 dark:bg-warning-900/30 hover:bg-warning-200 dark:hover:bg-warning-900/50 rounded-lg theme-transition"
            >
              Archivieren
            </button>
          </div>

          {/* Delete */}
          <div className="flex items-start justify-between p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Kurs löschen
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Alle Vorlagen und Session-Daten werden endgültig gelöscht.
              </p>
            </div>
            <button
              onClick={onDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 rounded-lg theme-transition"
            >
              Löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

CourseSettingsTab.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
    color: PropTypes.string
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool
};
